import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionClientesGet } from "../../redux/actions/clientes/clientes";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ClientesPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.clientes);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionClientesGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    conFactura: items.filter((c) => Boolean(c.rfc)).length,
    conSaldo: items.filter((c) => parseFloat(c.saldo_a_favor || 0) > 0).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_factura") return items.filter((c) => Boolean(c.rfc));
    if (activeFilter === "con_saldo") return items.filter((c) => parseFloat(c.saldo_a_favor || 0) > 0);
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de clientes", icon: <i className="fa-solid fa-address-book" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "con_factura", label: "Con datos fiscales", icon: <i className="fa-solid fa-file-invoice" />, iconCls: "paq-stat-icon-decoracion", count: counts.conFactura },
    { key: "con_saldo", label: "Con saldo a favor", icon: <i className="fa-solid fa-coins" />, iconCls: "paq-stat-icon-barra", count: counts.conSaldo },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Clientes</Title>
                <Text className="paq-subtitle">Catalogo de clientes de la tienda.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre del cliente..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Clientes ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} clientes encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/clientes/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nuevo cliente
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin clientes que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((c) => (
                        <div key={c.id_cliente} className="paq-card">
                          <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                            <i className="fa-solid fa-address-book paq-card-banner-icon" />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{c.nombre}</Paragraph>
                              {parseFloat(c.saldo_a_favor || 0) > 0 && (
                                <span className="paq-tipo-badge paq-tipo-decoracion">{money(c.saldo_a_favor)}</span>
                              )}
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-phone paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Telefono</span>
                                <span className="paq-info-value">{c.telefono || "No registrado"}</span>
                              </div>
                            </div>
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-file-invoice paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">RFC</span>
                                <span className="paq-info-value">{c.rfc || "Sin datos fiscales"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="paq-card-footer">
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/clientes/${c.id_cliente}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
